import React, { useState } from 'react';
import { Button, Card, Typography, Space, Alert, Spin } from 'antd';
import { LoginOutlined, UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAuth, useAuthError, useAuthLoading } from '../../auth/AuthContext';
import { useTheme } from '../../hooks/useTheme';
import './LoginPage.css';

const { Title, Text } = Typography;

/**
 * Beautiful login page that supports multiple authentication providers
 */
export const LoginPage: React.FC = () => {
  const { login, providerName } = useAuth();
  const error = useAuthError();
  const isLoading = useAuthLoading();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const { currentTheme } = useTheme();

  const handleLogin = async () => {
    setIsLoggingIn(true);
    
    try {
      await login();
    } catch (error) {
      logger.error('Login failed:', error);
      // Error is handled by the auth context
    } finally {
      setIsLoggingIn(false);
    }
  };

  const getProviderIcon = () => {
    switch (providerName.toLowerCase()) {
      case 'keycloak':
        return <UserOutlined />;
      case 'okta':
        return <UserOutlined />;
      case 'azure-ad':
        return <UserOutlined />;
      case 'google':
        return <UserOutlined />;
      case 'github':
        return <UserOutlined />;
      default:
        return <LoginOutlined />;
    }
  };

  const getProviderDisplayName = () => {
    switch (providerName.toLowerCase()) {
      case 'keycloak':
        return 'Keycloak';
      case 'okta':
        return 'Okta';
      case 'azure-ad':
        return 'Azure AD';
      case 'google':
        return 'Google';
      case 'github':
        return 'GitHub';
      default:
        return providerName;
    }
  };

  const getWelcomeMessage = () => {
    const time = new Date().getHours();
    if (time < 12) return 'Good morning!';
    if (time < 17) return 'Good afternoon!';
    return 'Good evening!';
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <img 
            src={currentTheme.branding.logoUrl} 
            alt={currentTheme.branding.appName} 
            className="login-logo"
            onError={(e) => {
              // Prevent infinite loop by checking if we've already tried the fallback
              if (e.currentTarget.src.includes("putki-logo.svg")) {
                // If putki-logo.svg also fails, remove the image entirely
                e.currentTarget.style.display = "none";
                return;
              }
              // Fallback if logo doesn't exist
              e.currentTarget.src = "/assets/putki-logo.svg";
            }}
          />
          <Title level={2} className="login-title">
            {currentTheme.branding.appName}
          </Title>
          <Text className="login-subtitle">
            {getWelcomeMessage()} Please sign in to continue.
          </Text>
        </div>

        <Card className="login-card" variant='outlined'>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {error && (
              <Alert
                message="Authentication Error"
                description={error}
                type="error"
                showIcon
                closable
              />
            )}

            <div className="login-form">
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div className="provider-info">
                  <LockOutlined className="provider-icon" />
                  <Text strong>
                    Sign in with {getProviderDisplayName()}
                  </Text>
                </div>

                <Button
                  type="primary"
                  size="large"
                  icon={getProviderIcon()}
                  onClick={handleLogin}
                  loading={isLoading || isLoggingIn}
                  disabled={isLoading || isLoggingIn}
                  className="login-button"
                  block
                >
                  {isLoading || isLoggingIn ? 'Signing in...' : `Sign in with ${getProviderDisplayName()}`}
                </Button>

                {(isLoading || isLoggingIn) && (
                  <div className="login-loading">
                    <Spin size="small" />
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      Connecting to authentication provider...
                    </Text>
                  </div>
                )}
              </Space>
            </div>

            <div className="login-footer">
              <Text type="secondary" className="login-help">
                Having trouble signing in? Please contact your administrator.
              </Text>
            </div>
          </Space>
        </Card>

      </div>
    </div>
  );
}; 